import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { SessionStats } from "@/features/sessions";
import { useScopedClient } from "@/features/clients/hooks/use-scoped-client";
import { useTranslation } from "@/features/utils/i18n";
import { ArrowRightIcon, MoonStarsIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

/**
 * The calls the agent took while the store was closed, and what was booked on
 * them: the part of the activity nobody on the team could have answered.
 *
 * The server only knows "closed" against opening hours somebody actually set;
 * until then (`after_hours: null`) the card says where to set them rather than
 * showing a zero that reads as "nobody called at night".
 */
export function AfterHours({
  stats,
  formatValue,
}: {
  stats: SessionStats | undefined;
  formatValue: (value: number) => string;
}) {
  const { t } = useTranslation("dashboard");
  const { clientId, scope } = useScopedClient();

  if (!stats || stats.after_hours === undefined) return null;
  const afterHours = stats.after_hours;

  const share =
    afterHours && afterHours.total_calls > 0
      ? Math.round((afterHours.calls / afterHours.total_calls) * 100)
      : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-purple/15 text-purple">
            <MoonStarsIcon weight="fill" className="size-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <CardTitle>{t("afterHours.title")}</CardTitle>
            <p className="text-xs text-muted-foreground">
              {t("afterHours.subtitle")}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {afterHours ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Figure
                value={afterHours.calls}
                label={t("afterHours.calls", { count: afterHours.calls })}
              />
              <Figure
                value={formatValue(afterHours.value)}
                label={t("afterHours.value")}
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
                <span>{t("afterHours.share")}</span>
                <span className="font-medium tabular-nums text-foreground">
                  {share}%
                </span>
              </div>
              <div
                className="h-2 w-full overflow-hidden rounded-full bg-muted"
                role="meter"
                aria-valuenow={share}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t("afterHours.share")}
              >
                <div
                  className="h-full rounded-full bg-gradient-to-r from-purple to-magenta"
                  style={{ width: `${share}%` }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>{t("afterHours.notConfigured")}</p>
            {/* The platform back-office mounts this dashboard read-only, on
                a client's file: no settings page to send anyone to there. */}
            {scope !== "staff" && (
              <Link
                to={`/${clientId}/settings/schedules`}
                className="inline-flex items-center gap-1 font-medium text-foreground hover:underline"
              >
                {t("afterHours.configure")}
                <ArrowRightIcon className="size-4" aria-hidden />
              </Link>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Figure({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <div className="rounded-lg bg-muted/50 px-3 py-2.5">
      <div className="text-2xl font-semibold tabular-nums tracking-tight">
        {value}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
